'use client';

import * as React from 'react';
import { cn } from '../utils/cn';

type SettingsLayoutProps = {
  children: React.ReactNode;
  onClose: () => void;
  className?: string;
  /** When true, switches to a vertical flex layout with no outer padding (for narrow sidebar context) */
  sidebar?: boolean;
};

type SettingsSidebarProps = {
  children: React.ReactNode;
  className?: string;
};

type SettingsNavItemProps = {
  children: React.ReactNode;
  isActive?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
};

const SettingsLayout = React.forwardRef<HTMLDivElement, SettingsLayoutProps>(
  ({ className, children, onClose, sidebar, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex h-full w-full relative bg-background overflow-hidden',
          sidebar ? 'flex-col p-0 gap-0 rounded-none' : 'flex-row rounded-2xl p-3 gap-3',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);
SettingsLayout.displayName = 'SettingsLayout';

const SettingsSidebar = React.forwardRef<HTMLDivElement, SettingsSidebarProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        // Inset nested container (Apple Ventura style)
        // Rounded card that floats inside parent with padding
        // Apple-style pop animation on mount
        'w-64 glass-card-bg glass-card-border rounded-2xl p-3 overflow-y-auto sidebar-animate-in',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
);
SettingsSidebar.displayName = 'SettingsSidebar';

type SettingsContentProps = {
  children: React.ReactNode;
  className?: string;
  /** Flat variant — no glass card, no border radius, tighter padding (for sidebar context) */
  flat?: boolean;
};

const SettingsContent = React.forwardRef<HTMLDivElement, SettingsContentProps>(
  ({ className, children, flat, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex-1 overflow-y-auto',
        flat ? 'px-4 py-3 bg-background' : 'glass-card rounded-2xl p-6',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
);
SettingsContent.displayName = 'SettingsContent';

const SettingsNavItem = React.forwardRef<HTMLButtonElement, SettingsNavItemProps>(
  ({ className, children, isActive, onClick, disabled, ...props }, ref) => (
    <button
      ref={ref}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={cn(
        // Base styles with liquid glass design and smooth transitions
        'cursor-pointer w-full text-left py-3 px-4 rounded-xl text-xl font-regular transition-all duration-300',
        // Default state
        'text-text-secondary hover:glass-surface-hover',
        // Active state - bright blue background with white text (Apple-like accent)
        isActive &&
          'bg-primary text-primary-foreground shadow-lg shadow-primary/20 hover:bg-primary',
        // Disabled state - muted colors and not-allowed cursor
        disabled &&
          'text-secondary-400 cursor-not-allowed hover:bg-transparent hover:text-secondary-400 opacity-50',
        // Focus state following design system
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-1',
        className
      )}
      {...props}
    >
      {children}
    </button>
  )
);
SettingsNavItem.displayName = 'SettingsNavItem';

export { SettingsContent, SettingsLayout, SettingsNavItem, SettingsSidebar };
